import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Checkbox } from '@/components/ui';
import { MessageSquare, Save, Edit3, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { commentAPI } from '../services/api';

interface CommentSectionProps {
  emailId: string;
}

interface CommentData {
  email_id: string;
  comment: string | null;
  include_in_ai: boolean;
}

export const CommentSection: React.FC<CommentSectionProps> = ({ emailId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [includeInAi, setIncludeInAi] = useState(false);
  
  const queryClient = useQueryClient();
  
  const { data: commentData } = useQuery({
    queryKey: ['comment', emailId],
    queryFn: async () => {
      const response = await commentAPI.get(emailId);
      return response.data;
    }
  });
  
  // Update local state when data changes
  useEffect(() => {
    if (commentData) {
      setCommentText(commentData.comment || '');
      setIncludeInAi(commentData.include_in_ai);
    }
  }, [commentData]);
  
  const updateMutation = useMutation({
    mutationFn: async ({ comment, includeInAi }: { comment: string; includeInAi: boolean }) => {
      const response = await commentAPI.update(emailId, comment, includeInAi);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comment', emailId] });
      setIsEditing(false);
      toast.success('Comment saved successfully');
    },
    onError: () => {
      toast.error('Failed to save comment');
    }
  });
  
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await commentAPI.delete(emailId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comment', emailId] });
      setCommentText('');
      setIncludeInAi(false);
      setIsEditing(false);
      toast.success('Comment deleted');
    },
    onError: () => {
      toast.error('Failed to delete comment');
    }
  });
  
  const handleSave = () => {
    if (commentText.trim()) {
      updateMutation.mutate({ comment: commentText.trim(), includeInAi });
    }
  };
  
  const handleDelete = () => {
    deleteMutation.mutate();
  };
  
  const hasComment = commentData?.comment && commentData.comment.trim();
  
  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="h-4 w-4 text-gray-600" />
        <span className="font-medium text-sm">Response Comment</span>
      </div>
      
      {!isEditing && !hasComment && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsEditing(true)}
          className="text-xs"
        >
          <Edit3 className="h-3 w-3 mr-1" />
          Add Comment
        </Button>
      )}
      
      {!isEditing && hasComment && (
        <div className="space-y-3">
          <div className="bg-white p-3 rounded border text-sm">
            {commentData?.comment}
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`include-ai-${emailId}`}
                checked={commentData?.include_in_ai || false}
                onCheckedChange={(checked: boolean) => {
                  setIncludeInAi(checked);
                  updateMutation.mutate({ 
                    comment: commentData?.comment || '', 
                    includeInAi: checked 
                  });
                }}
              />
              <label htmlFor={`include-ai-${emailId}`} className="text-xs text-gray-600">
                Include in AI regeneration
              </label>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCommentText(commentData?.comment || '');
                  setIsEditing(true);
                }}
                className="text-xs"
              >
                <Edit3 className="h-3 w-3 mr-1" />
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                className="text-xs text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {isEditing && (
        <div className="space-y-3">
          <Textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add your comment about this response..."
            className="min-h-[80px] text-sm"
          />
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`include-ai-edit-${emailId}`}
                checked={includeInAi}
                onCheckedChange={(checked: boolean) => setIncludeInAi(checked)}
              />
              <label htmlFor={`include-ai-edit-${emailId}`} className="text-xs text-gray-600">
                Include in AI regeneration
              </label>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  setCommentText(commentData?.comment || '');
                  setIncludeInAi(commentData?.include_in_ai || false);
                }}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!commentText.trim() || updateMutation.isPending}
                className="text-xs"
              >
                <Save className="h-3 w-3 mr-1" />
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
